import React, { useState, useMemo, useRef } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import AttributeTypes from "../../../Attributes/AttributeTypes";
import ErrorMessage from "../../../Shared/Error/ErrorMessage";
import { removeSpecialCharacters } from "../../../Entities/utils";
import DateTimeTypeField from "../../../Attributes/Fields/DateTimeTypeField";
import NumberTypeField from "../../../Attributes/Fields/NumberTypeField";
import { PicklistOptionsField } from "../../../Attributes/Fields/PicklistStringFields";
import TreeDropdown from "../../../Attributes/Fields/TreeDropdown";
import { CheckErrorInTree, removeExtraSpaceFromTreeElement, uuidv4 } from "../../../Attributes/Fields/TreeDropdownHelper";
import { isReservedAttributeName } from "../../../Attributes/reservedAttributeNames";

function AddNewPropertyDialog({ onClose, existingAttributes = [], t = (x) => x, doesAttributeExist, onAddNewAttribute, maxAttributesPerApp, currentAttributeCount }) {
    // Get translated types (fallback to Types if no i18n)
    const DataTypes = useMemo(() => {
        const types = AttributeTypes.getTranslatedTypes ? AttributeTypes.getTranslatedTypes(t) : AttributeTypes.Types;
        debugger;
        // Filter out 'Entity Lookup' and 'Mirror'
        return types.filter(type => type.value !== 'Entity Lookup' && type.value !== 'Mirror' && type.value !== 'Tree Dropdown' && type.value !== "Image" && type.value !== "Shared Lookup");
    }, [t]);
    const [attributeName, setAttributeName] = useState("");
    const [validName, setValidName] = useState(true);
    const [nameError, setNameError] = useState("");
    const [attributeType, setAttributeType] = useState(DataTypes[0]);
    const [dataTypes, setDataTypes] = useState(DataTypes);
    const [dateTimeType, setDateTimeType] = useState("date");
    const [numberType, setNumberType] = useState("integer");
    const [picklist, setPicklist] = useState("");
    const [picklistError, setPicklistError] = useState({ isValid: true, message: "" });
    const [treeDropdown, setTreeDropdown] = useState([
        {
            id: uuidv4(),
            text: "",
            parentId: null,
            items: [],
            root: true,
            expandedLevel: 1,
        },
    ]);
    const [treedropdownError, setTreedropdownError] = useState({ isValid: true, message: "" });
    const [maxAttributesError, setMaxAttributesError] = useState("");
    const attributeNameInput = useRef(null);

    // Validation logic similar to CreateAttributeDialog
    const handleName = (e) => {
        const value = e.target.value;
        const nameIsReserved = isReservedAttributeName(value);
        const nameIsBlank = value.trim() === "";
        // Use doesAttributeExist if provided, else fallback to old logic
        const nameInUse = doesAttributeExist
            ? doesAttributeExist(value)
            : existingAttributes.findIndex((attribute) => (
                removeSpecialCharacters(attribute.attributeName?.toLowerCase?.() || "") === removeSpecialCharacters(value.trim().toLowerCase())
            )) > -1;
        const nameContainsSys = /^\[SYS\]/i.test(value);
        const nameStartWithNumber = value.trim().match(/^\d/);
        const isNameLong = value.trim().length > 30;
        const valid = !nameIsBlank && !nameStartWithNumber && !nameInUse && !nameContainsSys && !nameIsReserved && !isNameLong;
        let error = "";
        if (nameIsBlank) error = t("errors.name_required");
        else if (nameStartWithNumber) error = t("errors.name_starts_with_number");
        else if (nameInUse) error = t("errors.name_in_use");
        else if (isNameLong) error = t("errors.name_too_long");
        else if (nameContainsSys) error = t("errors.name_contains_sys");
        else if (nameIsReserved) error = t("errors.name_is_reserved");
        setAttributeName(value);
        setValidName(valid);
        setNameError(error);
    };

    // ComboBox filter logic
    const handleTypeFilter = (e) => {
        let filtered = DataTypes;
        if (e.filter.value.trim() !== "") {
            filtered = DataTypes.filter((x) => x.text.toLowerCase().startsWith(e.filter.value.toLowerCase()));
        }
        setDataTypes(filtered);
    };

    // ComboBox change logic
    const handleTypeChange = (e) => {
        setAttributeType(e.target.value || DataTypes[0]);
    };

    // DateTime type change handler
    const handleDateTimeType = (e) => {
        setDateTimeType(e.value);
    };

    // Number type change handler
    const handleNumberType = (e) => {
        setNumberType(e.value);
    };

    // Picklist validation logic (simplified, can be extended)
    const checkPicklistIsProper = (data) => {
        const picklistValues = data
            .split("ɖ")
            .filter((option) => option.trim() !== "")
            .map((option) => option.trim());
        const picklistValuesMap = {};
        let picklistError = {
            isValid: true,
            message: "",
        };
        if (picklistValues.length === 0) {
            picklistError.isValid = false;
            return picklistError;
        }
        for (const value of picklistValues) {
            picklistValuesMap[value] = picklistValuesMap[value] ? ++picklistValuesMap[value] : 1;
            if (picklistValuesMap[value] > 1) {
                picklistError.isValid = false;
                picklistError.message = t("errors.duplicate_picklist_values");
                break;
            }
        }
        return picklistError;
    };
    const handleDropdownChange = (e) => {
        let error = checkPicklistIsProper(e.target.value);
        setPicklist(e.target.value);
        setPicklistError(error);
    };

    // TreeDropdown change handler
    const handleTreeDropdownChange = (data) => {
        const error = CheckErrorInTree(data);
        if (error !== "") {
            setTreedropdownError({ isValid: false, message: t("treedropdownerrors." + error, { ns: "attribute/tree_dropdown" }) });
        } else {
            setTreedropdownError({ isValid: true, message: "" });
        }
        setTreeDropdown(data);
    };

    // Custom item render for ComboBox
    const attributeTypeRender = (li, itemProps) => {
        const itemChildren = (
            <div className="items-center w-100 d-flex">
                <div style={{ width: "20px" }}>
                    <i className={itemProps.dataItem.icon}></i>
                </div>
                {itemProps.dataItem.text}
            </div>
        );
        return React.cloneElement(li, { tabIndex: 0 }, itemChildren);
    };

    // Validation for disabling create/save button (mirrors CreateAttributeDialog logic for these fields)
    const shouldCreateBeDisabled = () => {
        if (attributeName.trim() === "") return true;
        if (!validName) return true;
        if (attributeType.value === "Picklist String") {
            if (!picklistError.isValid) return true;
            if (picklist !== undefined && picklist.length === 0) return true;
        }
        if (attributeType.value === "Tree Dropdown") {
            if (!treedropdownError.isValid) return true;
            if (treeDropdown.length === 0) return true;
            if (treeDropdown[0].text === "") return true;
        }
        return false;
    };

    function handleAddNewAttribute() {
        if (typeof maxAttributesPerApp === 'number' && typeof currentAttributeCount === 'number') {
            if (currentAttributeCount >= maxAttributesPerApp) {
                setMaxAttributesError(t("errors.max_attributes_exceeded", { max: maxAttributesPerApp }, `Maximum of ${maxAttributesPerApp} attributes allowed`));
                return;
            }
        }
        setMaxAttributesError("");
        onAddNewAttribute({
            name: attributeName.trim(),
            type: convertToAppropriateType(attributeType.value),
            picklist: attributeType.value === "Picklist String" || attributeType.value === "State Field" ? picklist.split("ɖ").map(item => item.trim()).filter(item => !!item) : [],
            required: false,
            unique: false,
            checked: true,
            selected: true,
            disabled: false
        });
    }

    function convertToAppropriateType(type) {
        switch (type) {
            case "Number":
                if (numberType === "integer") return "Number_Integer";
                return "Number_Decimal";
            case "DateTime":
                if (dateTimeType === "date") return "DateTime_Date";
                if (dateTimeType === "time") return "DateTime_Time";
                return "DateTime";
            default:
                return type;
        }
    }
    
    function convertTypeToDesignerType(type) {
        switch (type) {
            case "string":
                return "String";
            case "number":
                return "Number_Integer";
            case "boolean":
                return "Boolean";
            case "date":
                return "DateTime_Date";
            case "reference":
                return "String";
            case "longtext":
                return "Multiline Text";
            case "email":
                return "Email";
            case "picklist":
                return "Picklist String";
            case "user":
                return "User Identity";
            case "state":
                return "State Field";
            default:
                return "String";
        }
    }

    return (
        <Dialog title={null} onClose={onClose} minWidth={500} className="add-object-dialog-custom">
            <div className="p-1 px-2">
                <div className="d-flex align-items-center justify-content-between mb-4">
                    <h5 className="fw-bold mb-0">{t("add_new_property_dialog.title")}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        style={{ zIndex: 2 }}
                        onClick={onClose}
                    />
                </div>
                <div className="mb-3">
                    <label className="form-label" required>{t("add_new_property_dialog.fields.name")}</label>
                    <input
                        maxLength={64}
                        onChange={handleName}
                        className={`form-control${!validName && attributeName ? ' is-invalid' : ''}`}
                        autoFocus={true}
                        type="text"
                        ref={attributeNameInput}
                        value={attributeName}
                    />
                    {!validName && attributeName && (
                        <div className="invalid-feedback d-block">{nameError}</div>
                    )}
                </div>
                <div className="mb-3">
                    <label className="form-label">{t("add_new_property_dialog.fields.type")}</label>
                    <ComboBox
                        className="form-control-custom"
                        data={dataTypes}
                        textField="text"
                        itemRender={attributeTypeRender}
                        onFilterChange={handleTypeFilter}
                        filterable={true}
                        onChange={handleTypeChange}
                        suggest={true}
                        allowCustom={false}
                        value={attributeType}
                    />
                </div>
                {attributeType.value === "DateTime" && (
                    <div className="mb-3">
                        <DateTimeTypeField value={dateTimeType} onChange={handleDateTimeType} />
                    </div>
                )}
                {attributeType.value === "Number" && (
                    <div className="mb-3">
                        <NumberTypeField value={numberType} onChange={handleNumberType} />
                    </div>
                )}
                {(attributeType.value === "Picklist String" || attributeType.value === "State Field") && (
                    <div className="mb-3">
                        <label className="form-label">
                            {t("add_new_property_dialog.fields.dropdown_values_label")}
                        </label>
                        <PicklistOptionsField
                            onChange={handleDropdownChange}
                            value={picklist}
                            onErr={() => {
                                setPicklistError({ isValid: false, message: t("errors.invalid_picklist") });
                            }}
                            autoFocus={true}
                        />
                        {picklistError.isValid === false && (
                            <div className="invalid-feedback d-block">{picklistError.message}</div>
                        )}
                    </div>
                )}
                {attributeType.value === "Tree Dropdown" && (
                    <div className="mb-3">
                        <label className="form-label">
                            {t("add_new_property_dialog.fields.tree_dropdown_label")}
                        </label>
                        <TreeDropdown
                            isNew={true}
                            onUpdate={handleTreeDropdownChange}
                            defaultTreeDropdown={treeDropdown}
                        />
                        {!treedropdownError.isValid && (
                            <div className="invalid-feedback d-block">{treedropdownError.message}</div>
                        )}
                    </div>
                )}
                {maxAttributesError && (
                    <div className="text-danger mb-2" role="alert">{maxAttributesError}</div>
                )}
                <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                        className="btn btn-secondary px-4"
                        type="button"
                        onClick={onClose}
                    >
                        {t("add_new_property_dialog.cancel")}
                    </button>
                    <button
                        className="btn btn-primary px-4"
                        type="submit"
                        disabled={shouldCreateBeDisabled()}
                        onClick={handleAddNewAttribute}
                    >
                        {t("add_new_property_dialog.add")}
                    </button>
                </div>
            </div>
        </Dialog>
    );
}

export default AddNewPropertyDialog;
